import CouponApi from '@apis/coupon/CouponApi';
import couponQueries from '@apis/queries/couponQueries';
import useToast from '@hooks/ui/useToast';
import { PaperCouponResponse } from '@models/coupon';
import { ErrorResponse } from '@models/error';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

function useRegisterCoupon() {
    const { t } = useTranslation('coupon');
    const [code, setCode] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>();
    const [coupon, setCoupon] = useState<PaperCouponResponse>();
    const toast = useToast();
    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: CouponApi.registerPaperCoupon,
        onSuccess() {
            setCoupon(undefined);
            setCode('');
            queryClient.invalidateQueries({ queryKey: couponQueries._def });
            toast.success(t('paperCoupon.success'));
        },
        onError(e: AxiosError<ErrorResponse>) {
            switch (e.response?.status) {
            case 404:
                toast.error(t('paperCoupon.error.notFound'));
                break;
            case 409:
                toast.error(t('paperCoupon.error.alreadyUsed'));
                break;
            default:
                toast.error(t('paperCoupon.error.default'));
            }
        },
    });

    const handleSetCode = (e: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage(undefined);
        setCode(e.target.value);
    };

    const handleGetCoupon = () => {
        if (code.length !== 12) {
            setErrorMessage(t('paperCoupon.error.invalidLength'));
            return;
        }
        CouponApi.getPaperCoupon(code)
            .then(setCoupon)
            .catch((e: AxiosError) => {
                switch (e.response?.status) {
                case 400:
                case 404:
                    toast.error(t('paperCoupon.error.notFound'));
                    break;
                case 409:
                    toast.error(t('paperCoupon.error.alreadyUsed'));
                    break;
                default:
                    toast.error(t('paperCoupon.error.default'));
                }
            });
    };

    const handleRegisterCoupon = () => {
        mutate(code);
    };

    return {
        errorMessage,
        code,
        handleSetCode,
        handleGetCoupon,
        coupon,
        handleRegisterCoupon,
    };
}

export default useRegisterCoupon;
